import { useEffect, useState } from 'react'
import { Badge, Group, Stack, Text } from '@mantine/core'
import { IconRobot } from '@tabler/icons-react'
import { useLocation } from 'react-router-dom'

import { DataCard, EmptyState } from '@/components/ui'
import { useMeuRobo, type Robo } from '@/api/robo'
import { estadoDoRobo } from './estado'
import { EscopoDoRobo } from './EscopoDoRobo'
import { AvataresDoRobo } from './MeuRoboAparencia'
import { SomDoRobo } from './MeuRoboSom'
import { FontesDoRobo } from './MeuRoboNotificacoes'
import { NomeDoMeuRobo } from './NomeDoMeuRobo'
import { PublicacoesDoRobo } from './PublicacoesDoRobo'
import { RestricoesDoRobo } from './RestricoesDoRobo'
import { PassoRede } from './recebimento/PassoRede'
import { WizardDeRecebimento } from './recebimento/WizardDeRecebimento'
import { emWizard } from './recebimento/passos'

/**
 * `/robo/meu` (spec 2026-09-27, 4.4/4.5). Com pendência obrigatória — o
 * recebimento, o nome ou o escopo —, o wizard; sem ela, a tela única com as
 * sete seções. O wizard que já estava aberto nesta sessão continua até o fim,
 * mesmo que o último obrigatório tenha sido cumprido no meio.
 */
export function MeuRobo() {
  const { robo, carregando } = useMeuRobo()
  const [emCurso, setEmCurso] = useState(false)
  const wizard = robo ? emWizard(robo.recebimento, emCurso) : false

  useEffect(() => { if (wizard && !emCurso) setEmCurso(true) }, [wizard, emCurso])

  if (carregando) return <Text size="sm" c="dimmed">Carregando…</Text>

  if (!robo) {
    return (
      <EmptyState
        icon={IconRobot}
        title="Você ainda não tem um robô"
        description="Robôs são atribuídos por quem administra a frota. Peça a quem cuida dos robôs do seu cliente — é essa pessoa que faz a atribuição, e a partir daí este espaço é seu."
      />
    )
  }

  if (wizard) return <WizardDeRecebimento robo={robo} aoConcluir={() => setEmCurso(false)} />
  return <TelaUnica robo={robo} />
}

function Secao({ id, children }: { id: string; children: React.ReactNode }) {
  return <section id={id} style={{ scrollMarginTop: 72 }}>{children}</section>
}

function TelaUnica({ robo }: { robo: Robo }) {
  const { hash } = useLocation()
  const e = estadoDoRobo(robo)

  // `/robo/meu/notificacoes` e `/aparencia` redirecionam para a âncora.
  useEffect(() => {
    if (!hash) return
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hash])

  return (
    <Stack gap="md">
      <Secao id="estado">
        <DataCard title="O seu robô">
          <Group gap="md" wrap="nowrap" align="flex-start">
            <Text size="sm" c="dimmed" w={190} style={{ flexShrink: 0 }}>Estado</Text>
            <Stack gap={2}>
              <Badge color={e.cor} variant="light">{e.rotulo}</Badge>
              <Text size="xs" c="dimmed">{e.significado}</Text>
            </Stack>
          </Group>
        </DataCard>
      </Secao>
      <Secao id="nome">
        <DataCard title="Nome"><NomeDoMeuRobo robo={robo} /></DataCard>
      </Secao>
      <Secao id="rede">
        <DataCard title="Rede"><PassoRede robo={robo} aoConfigurar={() => undefined} /></DataCard>
      </Secao>
      <Secao id="escopo">
        {/* De onde vêm os alertas é o dono quem escolhe (spec 2026-09-24). */}
        <EscopoDoRobo rota={robo.rota ?? null} tenantDoCadastro={robo.tenant_name}
          modelo={robo.hardware_model} />
      </Secao>
      {/* O que o robô nunca propõe (spec 2026-09-30-robo-agente-pessoal §4.1). */}
      <Secao id="nunca"><RestricoesDoRobo nome={robo.name} /></Secao>
      {/* Onde ele avisa crítico no grupo (spec 2026-09-30-robo-agente-pessoal §5.3). */}
      <Secao id="grupo"><PublicacoesDoRobo nome={robo.name} /></Secao>
      <Secao id="alertas"><FontesDoRobo robo={robo} /></Secao>
      <Secao id="aparencia"><AvataresDoRobo robo={robo} /></Secao>
      <Secao id="som"><SomDoRobo robo={robo} /></Secao>
    </Stack>
  )
}
